// Everything about a video that is not a file.
//
// Editing is a dirty buffer, not a field-at-a-time save: type freely, then one
// SAVE commits every change as a single partial patch. The old admin had a
// per-field edit/save icon pair driven by one global cursor, so editing two
// fields meant two round trips and losing the first if you forgot to click.
//
// The buffer itself lives in the detail pane (see useVideoForm) so that
// switching tabs cannot quietly discard it.

import React, { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAdmin } from '../AdminStore';
import { describeError } from '../api';
import { FIELDS, extraFieldKeys } from '../schema';
import { parseProducts } from '../../ps/format';

function FieldLabel({ field }) {
  return (
    <Box sx={{ mb: '4px' }}>
      <span className="admin-label">{field.label}</span>
      {field.help && (
        <Typography sx={{ fontSize: 10.5, color: 'var(--ps-text-dim)', mt: '1px' }}>
          {field.help}
        </Typography>
      )}
    </Box>
  );
}

export function OverviewTab({ identifier, video, buffer }) {
  const { patch, deleteFields, toast, knownCategories, knownProducts } = useAdmin();
  const { form, set, revert, save, saving, error, isDirty } = buffer;

  const [addField, setAddField] = useState({ open: false, key: '', value: '' });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const extras = extraFieldKeys(video);

  const submitAddField = async () => {
    const key = addField.key.trim();
    if (!key) return;
    try {
      await patch(identifier, { [key]: addField.value });
      setAddField({ open: false, key: '', value: '' });
      toast(`Added "${key}".`, 'success');
    } catch (err) {
      toast(describeError(err, 'The field was not added.'), 'error');
    }
  };

  const removeField = async (key) => {
    try {
      // Needs the explicit delete channel: addupdate merges, so leaving the key
      // out of a patch would silently keep it.
      await deleteFields(identifier, [key]);
      setConfirmDelete(null);
      toast(`Deleted "${key}".`, 'success');
    } catch (err) {
      toast(describeError(err, 'The field was not deleted.'), 'error');
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      {FIELDS.map((field) => (
        <Box key={field.key} sx={{ mb: '18px' }}>
          <FieldLabel field={field} />

          {(field.type === 'text' || field.type === 'longtext') && (
            <TextField
              fullWidth
              size="small"
              multiline={field.type === 'longtext'}
              minRows={field.type === 'longtext' ? 3 : undefined}
              value={form[field.key]}
              onChange={(e) => set(field.key, e.target.value)}
              inputProps={{ 'aria-label': field.label }}
            />
          )}

          {field.type === 'category' && (
            <Autocomplete
              freeSolo
              size="small"
              options={knownCategories}
              value={form[field.key]}
              onInputChange={(_, v) => set(field.key, v || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{ ...params.inputProps, 'aria-label': field.label }}
                />
              )}
            />
          )}

          {field.type === 'products' && (
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={knownProducts}
              value={form[field.key]}
              // Through parseProducts, not raw: freeSolo will hand back the
              // same name twice if it is typed twice.
              onChange={(_, v) => set(field.key, parseProducts(v))}
              renderTags={(list, getTagProps) =>
                list.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return <Chip key={key} label={option} size="small" {...tagProps} />;
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Add a product"
                  inputProps={{ ...params.inputProps, 'aria-label': field.label }}
                />
              )}
            />
          )}

          {(field.type === 'flag' || field.type === 'optout') && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {/* MUI v7 routes input attributes through slotProps; the old
                  `inputProps` is silently dropped, taking the label with it. */}
              <Switch
                checked={!!form[field.key]}
                onChange={(e) => set(field.key, e.target.checked)}
                slotProps={{ input: { 'aria-label': field.label } }}
              />
              <span className="admin-value" style={{ fontSize: 12 }}>
                {form[field.key] ? 'on' : 'off'}
              </span>
            </Box>
          )}
        </Box>
      ))}

      {error && <Alert severity="error" sx={{ mb: '10px' }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        <Button variant="contained" disabled={!isDirty || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button disabled={!isDirty || saving} onClick={revert}>
          Revert
        </Button>
        {isDirty && !saving && (
          <Typography sx={{ fontSize: 10.5, color: 'var(--ps-gold)', ml: '6px' }}>
            unsaved changes · ⌘S
          </Typography>
        )}
      </Box>

      <hr className="admin-rule" />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '10px' }}>
        <span className="admin-label">Other fields</span>
        <Button
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={() => setAddField({ open: true, key: '', value: '' })}
        >
          Add field
        </Button>
      </Box>

      {extras.length ? (
        extras.map((key) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '4px' }}>
            <TextField
              fullWidth
              size="small"
              label={key}
              defaultValue={String(video[key] ?? '')}
              onBlur={(e) => {
                if (e.target.value !== String(video[key] ?? '')) {
                  patch(identifier, { [key]: e.target.value }).catch((err) =>
                    toast(describeError(err, 'Nothing was saved.'), 'error'),
                  );
                }
              }}
            />
            {/* Quiet until hover: one click from losing a field. */}
            <Button
              variant="text"
              aria-label={`Delete field ${key}`}
              onClick={() => setConfirmDelete(key)}
              sx={{ minWidth: 34, px: 0, '&:hover': { color: 'var(--ps-mute)' } }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </Button>
          </Box>
        ))
      ) : (
        <Typography sx={{ fontSize: 11.5, color: 'var(--ps-text-dim)' }}>
          None. Anything added here is stored on the record but read by nothing on the site
          unless you wire it up.
        </Typography>
      )}

      <Dialog open={addField.open} onClose={() => setAddField({ open: false, key: '', value: '' })} fullWidth maxWidth="sm">
        <DialogTitle>Add field</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            label="Field name"
            sx={{ mb: '10px' }}
            value={addField.key}
            onChange={(e) => setAddField((p) => ({ ...p, key: e.target.value }))}
          />
          <TextField
            fullWidth
            size="small"
            label="Value"
            value={addField.value}
            onChange={(e) => setAddField((p) => ({ ...p, value: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddField({ open: false, key: '', value: '' })}>Cancel</Button>
          <Button variant="contained" onClick={submitAddField} disabled={!addField.key.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete field</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Remove "{confirmDelete}" from {identifier}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => removeField(confirmDelete)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OverviewTab;
